import { motion } from 'framer-motion';
import { useEffect } from 'react';

function SuccessStoriesHero() {
  return (
    <section className="relative overflow-hidden py-28">
      {/* Subtle background */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(59,130,246,0.12),transparent_60%)]" />
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(59,130,246,0.06)_1px,transparent_1px),linear-gradient(to_bottom,rgba(59,130,246,0.06)_1px,transparent_1px)] bg-[size:64px_64px]" />
      </div>

      <div className="mx-auto max-w-5xl px-6 text-left">
        <span className="animate-fadeUp mb-4 inline-block text-xs font-semibold uppercase tracking-wider text-blue-600 dark:text-blue-400">
          Proven in Production
        </span>

        <h1 className="animate-fadeUp text-4xl font-extrabold tracking-tight text-gray-900 delay-150 sm:text-5xl md:text-6xl dark:text-gray-100">
          Client
          <span className="animate-fadeUp block bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
            Success
          </span>{' '}
          Stories
        </h1>

        <p className="animate-fadeUp mt-6 max-w-3xl text-lg leading-relaxed text-gray-700 md:text-xl dark:text-gray-300">
          How we partner with teams to solve real-world problems using AI, data, fintech, and
          telecom engineering.
        </p>
      </div>
    </section>
  );
}

function CaseStudyIndex() {
  const caseStudies = [
    {
      slug: 'upresponse-ai-it-support-platform',
      name: 'UpResponse',
      client: 'UpResponse Pvt Ltd',
      tagline: 'AI-Powered Automated IT Support Platform',
      website: 'https://upresponse.io',
      industry: ['B2B SaaS', 'IT Operations', 'Enterprise Support'],
      technologies: ['Agentic AI', 'Knowledge Graphs', 'MERN Stack', 'Azure'],
      summary:
        'UpResponse is an AI-driven IT support automation platform designed to eliminate manual ticket handling across large and distributed support teams.',
      problem:
        'Organizations managing multiple IT support teams struggled with slow response times, high operational costs, and fragmented ticket workflows across channels.',
      solution:
        'We engineered an end-to-end automated ticketing system powered by agentic AI. Users can create tickets and receive instant resolutions across multiple channels through an intelligent knowledge base that continuously learns from historical data.',
      outcome:
        'UpResponse enables businesses to significantly reduce IT support costs while improving response speed, reliability, and service consistency at scale.',
      metrics: [
        'End-to-End Automation',
        'Multi-Channel Support',
        'Cost Reduction',
        'AI-Driven Knowledge Base',
      ],
      gradient: 'from-blue-500 via-indigo-500 to-purple-600',
    },
    {
      slug: 'dialog-axiata-free-data-qr-platform',
      name: 'Free Data Platform',
      client: 'Dialog Axiata PLC',
      tagline: 'QR-Based Telco Engagement System',
      website: 'https://dialog.lk',
      industry: ['Telecommunications', 'Public Engagement', 'Smart Infrastructure'],
      technologies: ['QR Systems', 'Backend APIs', 'Scalable Event Infrastructure'],
      summary:
        'A large-scale QR-based platform built for Dialog Axiata PLC to distribute free mobile data during Vesak and Poson seasons.',
      problem:
        'Delivering time-limited mobile data benefits to large crowds required a secure, scalable, and frictionless system that could operate reliably under high traffic.',
      solution:
        'We designed a QR-based distribution platform allowing users to instantly claim free mobile data by scanning codes deployed across selected locations in Colombo and Anuradhapura.',
      outcome:
        'The platform served thousands of users during peak events, demonstrating high availability, fast response times, and smooth telco-grade scalability.',
      metrics: [
        'High Traffic Handling',
        'Instant QR Claims',
        'Scalable Architecture',
        'Telco-Grade Reliability',
      ],
      gradient: 'from-cyan-400 via-blue-400 to-indigo-500',
    },
    {
      slug: 'rushreports-ai-legal-intelligence',
      name: 'RushReports',
      client: 'JAAR Law',
      tagline: 'AI-Driven Legal Intelligence Platform',
      website: 'https://rushreports.lk',
      industry: ['LegalTech', 'Regulatory Intelligence', 'AI Automation'],
      technologies: ['NLP', 'Document Classification', 'AI Summarization'],
      summary:
        'RushReports monitors, analyzes, and summarizes legislative changes in Sri Lanka in real time using AI.',
      problem: 'Manual tracking of legal updates was slow, error-prone, and difficult to scale.',
      solution:
        'We implemented an AI pipeline that continuously monitors official sources, detects new legal updates, classifies content, and generates verified summaries automatically.',
      outcome:
        'The platform transformed slow manual workflows into automated, reliable legal intelligence with faster and more accurate updates.',
      metrics: [
        'Real-Time Monitoring',
        'Automated Summaries',
        'Error Reduction',
        'Scalable Legal Intelligence',
      ],
      gradient: 'from-yellow-400 via-orange-400 to-red-500',
    },
    {
      slug: 'fitness-leaf-ai-recipe-saas',
      name: 'Fitness Leaf AI',
      client: '',
      tagline: 'AI-Powered Personalized Nutrition SaaS',
      website: '',
      industry: ['HealthTech', 'AI SaaS', 'Nutrition'],
      technologies: ['AI Prompt Systems', 'Web SaaS', 'Preference Modeling'],
      summary:
        'Fitness Leaf AI generates vegetarian recipes tailored to individual dietary preferences, powered by AI-driven personalization.',
      problem:
        'The client needed a lightweight, cost-effective AI solution to provide customized nutrition guidance without complex infrastructure.',
      solution:
        'We built an AI-powered recipe platform that adapts outputs based on user preferences, dietary restrictions, and ingredient availability.',
      outcome:
        'The product allowed fast launch with minimal overhead while delivering highly personalized, AI-generated nutrition content.',
      metrics: ['Personalized Recipes', 'Dietary Adaptation', 'AI Automation', 'Quick Deployment'],
      gradient: 'from-green-400 via-teal-400 to-blue-500',
    },
    {
      slug: 'gymly-ai-gym-management-system',
      name: 'GYMLY',
      client: 'Team Akwid Labs',
      tagline: 'AI-Powered Gym Management System',
      website: '',
      industry: ['FitnessTech', 'SaaS', 'Operations Automation'],
      technologies: ['MERN Stack', 'Azure', 'AI Automation'],
      summary:
        'GYMLY is an AI-powered gym management platform that automates planning, scheduling, and personalized coaching.',
      problem:
        'Fragmented systems for scheduling, member management, and coaching created inefficiencies and higher operational costs.',
      solution:
        'We built an integrated AI-assisted platform automating planning, scheduling, member tracking, and coaching workflows.',
      outcome:
        'Gym owners can now operate smarter, reduce manual work, and deliver a more personalized fitness experience at scale.',
      metrics: [
        'AI Scheduling',
        'Member Management',
        'Personalized Coaching',
        'Operational Efficiency',
      ],
      gradient: 'from-pink-400 via-purple-400 to-indigo-500',
    },
    {
      slug: 'upcoming-payment-solution',
      name: "Don't Have a Name Yet",
      client: 'Team Akwid Labs',
      tagline: 'Enterprise Payment Processing Platform',
      website: '',
      industry: ['FinTech', 'Payment Processing', 'Financial Services'],
      technologies: ['Blockchain', 'Payment APIs', 'Security Infrastructure', 'Microservices'],
      summary:
        'A secure, scalable payment gateway platform designed for enterprise-level financial transactions with multi-currency support and real-time fraud detection.',
      problem:
        'Traditional payment systems lacked the security, scalability, and flexibility needed for modern financial operations, with high transaction fees and limited integration capabilities.',
      solution:
        'We are developing a comprehensive payment processing platform with advanced encryption, blockchain integration, and AI-powered fraud detection to enable secure, fast, and cost-effective transactions.',
      outcome: "We're eagerly waiting to launch this revolutionary product with you!",
      metrics: [
        'Multi-Currency Support',
        'Real-Time Fraud Detection',
        'Enterprise Security',
        'In Development',
      ],
      gradient: 'from-emerald-500 via-teal-500 to-cyan-600',
    },
    {
      slug: 'Under-Construction',
      name: 'Not Published Yet',
      client: '',
      tagline: 'Edu-Tech Project in Development',
      website: '',
      industry: ['EduTech', 'AI Learning Platforms'],
      technologies: ['AI Tutoring Systems', 'Adaptive Learning'],
      summary:
        'An AI-powered educational platform designed to provide personalized learning experiences.',
      problem:
        'Traditional educational platforms often fail to adapt to individual learning styles and paces, leading to disengagement and suboptimal outcomes.',
      solution:
        'We are building an adaptive learning platform that uses AI to tailor content and pacing to each student’s unique needs, enhancing engagement and improving learning outcomes.',
      outcome: "We're excited to share this innovative educational solution with you soon!",
      metrics: ['Personalized Learning', 'Adaptive Content', 'AI Tutoring', 'In Development'],
      gradient: 'from-yellow-400 via-orange-400 to-red-500',
    },
  ];

  return (
    <motion.section
      className="relative mx-auto max-w-6xl px-6 py-28"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      variants={{
        hidden: {},
        visible: {},
      }}
    >
      {/* Background */}
      <div className="absolute inset-0 -z-10">
        {/* <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(59,130,246,0.15),transparent_60%)]" /> */}
      </div>

      {/* Header */}
      <motion.div
        className="mb-24 max-w-3xl"
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.1 }}
        viewport={{ once: true }}
      >
        <span className="mb-4 inline-block text-xs font-semibold uppercase tracking-wider text-indigo-500 dark:text-indigo-400">
          Case Studies
        </span>

        <h2 className="text-4xl font-extrabold tracking-tight text-gray-900 sm:text-4xl dark:text-gray-100">
          Some of
          <span className="block bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
            Our Favorite Projects
          </span>
        </h2>

        <p className="mt-4 text-base leading-relaxed text-gray-600 sm:text-lg dark:text-gray-300">
          Discover real client success stories showcasing innovative AI, fintech, and telecom
          solutions that delivered measurable business results and enhanced user experiences across
          diverse industries.
        </p>
      </motion.div>

      {/* Case cards */}
      <motion.div
        className="space-y-10"
        initial="hidden"
        animate="visible"
        variants={{
          hidden: {},
          visible: {},
        }}
      >
        {caseStudies.map((c, idx) => (
          <motion.article
            key={c.slug}
            className={`relative overflow-hidden rounded-3xl bg-gradient-to-r ${c.gradient || 'from-blue-400 to-indigo-500'} shadow-xl transition-transform duration-300 hover:scale-[1.02]`}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 + idx * 0.1 }}
            viewport={{ once: true, amount: 0.2 }}
            whileHover={{ scale: 1.025 }}
          >
            {/* Inner card */}
            <motion.div
              className="flex w-full flex-col gap-8 bg-white/95 p-6 lg:flex-row lg:p-10 dark:bg-slate-950/90"
              initial={false}
              animate={false}
            >
              {/* Left: Main Content */}
              <div className="flex flex-1 flex-col gap-6">
                {/* Client & Website */}
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-sm font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    {c.client}
                  </span>
                  {c.website && (
                    <motion.a
                      href={c.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-semibold text-indigo-600 hover:text-indigo-500"
                      whileHover={{ scale: 1.08 }}
                      transition={{ type: 'spring', stiffness: 300 }}
                    >
                      Visit Website
                    </motion.a>
                  )}
                </div>

                {/* Project Name */}
                <motion.h3
                  className="text-2xl font-extrabold leading-tight text-gray-900 sm:text-3xl dark:text-gray-100"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 + idx * 0.05 }}
                  viewport={{ once: true }}
                >
                  {c.name}
                </motion.h3>
                <p className="text-md italic text-gray-600 sm:text-lg dark:text-gray-300">
                  {c.tagline}
                </p>

                {/* Sections: Summary, Problem, Solution, Outcome */}
                <div className="flex flex-col gap-4">
                  {['summary', 'problem', 'solution', 'outcome'].map((section, sidx) => {
                    // Map each section to a gradient or color
                    const borderColors = [
                      'border-indigo-100',
                      'border-purple-500',
                      'border-pink-500',
                      'border-green-500',
                    ];
                    return (
                      <motion.div
                        key={section}
                        className={`flex flex-col gap-1 rounded-lg ${borderColors[sidx]} border-l-4 bg-gray-50 p-4 dark:bg-gray-800/20`}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: 0.3 + sidx * 0.08 + idx * 0.03 }}
                        viewport={{ once: true }}
                      >
                        <h4 className="text-sm font-bold uppercase tracking-wider text-gray-800 dark:text-gray-100">
                          {section.charAt(0).toUpperCase() + section.slice(1)}
                        </h4>
                        <p className="text-sm leading-relaxed text-gray-700 sm:text-base dark:text-gray-300">
                          {c[section as keyof typeof c]}
                        </p>
                      </motion.div>
                    );
                  })}
                </div>
                {/* Key Metrics */}
                {c.metrics?.length > 0 && (
                  <motion.div
                    className="mt-6"
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.45 + idx * 0.05 }}
                    viewport={{ once: true }}
                  >
                    <h4 className="mb-3 text-sm font-bold uppercase tracking-wider text-gray-800 dark:text-gray-100">
                      Key Results
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {c.metrics.map((metric: string, mIdx: number) => (
                        <motion.span
                          key={metric}
                          className={`inline-flex items-center rounded-full px-4 py-2 text-xs font-semibold shadow-sm ${
                            metric === 'In Development'
                              ? 'bg-gradient-to-r from-red-50 to-rose-50 text-red-700 dark:from-red-500/10 dark:to-rose-500/10 dark:text-red-300'
                              : 'bg-gradient-to-r from-emerald-50 to-teal-50 text-emerald-700 dark:from-emerald-500/10 dark:to-teal-500/10 dark:text-emerald-300'
                          }`}
                          initial={{ opacity: 0, scale: 0.8 }}
                          whileInView={{ opacity: 1, scale: 1 }}
                          transition={{ duration: 0.3, delay: 0.5 + mIdx * 0.05 + idx * 0.01 }}
                          viewport={{ once: true }}
                        >
                          {metric}
                        </motion.span>
                      ))}
                    </div>
                  </motion.div>
                )}
              </div>

              {/* Right: Quick Info */}
              <motion.div
                className="flex flex-shrink-0 flex-col gap-4 lg:w-64"
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.25 + idx * 0.05 }}
                viewport={{ once: true }}
              >
                {/* Industry Tags */}
                <div className="flex flex-wrap gap-2">
                  {c.industry.map((tag) => (
                    <motion.span
                      key={tag}
                      className="rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-700 dark:bg-blue-500/10 dark:text-blue-300"
                      initial={{ opacity: 0, scale: 0.8 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.3, delay: 0.35 + idx * 0.03 }}
                      viewport={{ once: true }}
                    >
                      {tag}
                    </motion.span>
                  ))}
                </div>
              </motion.div>
            </motion.div>
          </motion.article>
        ))}
      </motion.div>
    </motion.section>
  );
}

const clients = [
  {
    src: 'Images/Clients/Dialog-logo (2).png',
    alt: 'Dialog Axiata PLC',
    href: 'https://www.dialog.lk/',
  },
  {
    src: 'Images/Clients/upresponse-logo.png',
    alt: 'UpResponse.io',
    href: 'https://upresponse.io/',
  },
  {
    src: 'Images/Clients/Fitness-Leaf-AI-logo-Transparent-bg-400x100-1.png',
    alt: 'Fitness Leaf AI',
  },
  {
    src: 'Images/Clients/hagos_marketing_logo-_blue_transparent_background__1_.png',
    alt: 'Hagos Marketing',
  },
  {
    src: 'Images/Clients/otago-polytechnic-horizontal-blue.png',
    alt: 'Otago Polytechnic',
    href: 'https://www.otago.ac.nz/',
  },
  { src: 'Images/Clients/orpheus-digital.png', alt: 'Orpheus Digital' },
  { src: 'Images/Clients/jaar-law-favicon.png', alt: 'Jaar Law', href: 'https://www.jaarlaw.com/' },
];

function ClosingCredibility() {
  return (
    <section className="relative mx-auto max-w-6xl overflow-hidden px-6 py-20 text-center">
      {/* Background */}
      <div className="absolute inset-0 -z-10">
        {/* Radial glow */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(139,92,246,0.18),transparent_60%)]" />
        {/* Grid */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(139,92,246,0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgba(139,92,246,0.08)_1px,transparent_1px)] bg-[size:64px_64px]" />
      </div>

      <div className="mx-auto max-w-6xl px-6 text-center">
        {/* Header */}
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-4xl font-extrabold text-gray-900 sm:text-5xl dark:text-gray-100"
        >
          Join the Success Stories
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          viewport={{ once: true }}
          className="mt-4 text-lg leading-relaxed text-gray-700 sm:text-xl dark:text-gray-300"
        >
          We've partnered with clients across AI, FinTech, Telco, and more, delivering measurable
          impact and innovation. If you'd like your project featured here, reach out and let's
          collaborate.
        </motion.p>

        {/* CTA Button */}
        <motion.a
          href="/contacts"
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="mt-8 inline-block rounded-full bg-gradient-to-r from-purple-600 to-pink-600 px-8 py-3 text-lg font-semibold text-white shadow-lg transition-transform duration-300 hover:shadow-[0_0_25px_rgba(168,85,247,0.6)]"
        >
          Submit Your Project
        </motion.a>

        {/* Client logos: horizontal, small */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          viewport={{ once: true }}
          className="mt-12 flex flex-wrap items-center justify-center gap-8"
        >
          {clients.map((client, idx) => {
            const logo = (
              <motion.img
                key={idx}
                src={client.src}
                alt={client.alt}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, delay: 0.4 + idx * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.1 }}
                className="h-10 object-contain transition duration-300 sm:h-12"
              />
            );
            return client.href ? (
              <a
                key={idx}
                href={client.href}
                target="_blank"
                rel="noopener noreferrer"
                className="flex justify-center"
              >
                {logo}
              </a>
            ) : (
              <div key={idx} className="flex justify-center">
                {logo}
              </div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}

export default function SuccessStories() {
  useEffect(() => {
    document.title = 'Akwid Labs | Client Success Stories in AI, FinTech, and Telecom Engineering';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        'Akwid Labs works with clients across AI, FinTech, and Telecom engineering from countries including Sri Lanka, USA, UK, Switzerland, Australia, and New Zealand to deliver innovative solutions that drive measurable business impact. Explore our client success stories showcasing real-world results and transformative projects.',
      );
    }
  }, []);

  return (
    <div className="mx-auto sm:px-6 lg:px-8">
      <SuccessStoriesHero />
      <CaseStudyIndex />
      <ClosingCredibility />
    </div>
  );
}
